/**
 * Moteur d'adhésion Shotokan AAMC — statuts, complétude, activation.
 * Pas de booléen isMember : le statut est la source de vérité.
 */
import {
  MEMBERSHIP_STATUSES,
  PAYMENT_STATUSES,
  REGISTRATION_KIND,
  TEMP_PERIOD_POLICY,
} from "./membershipConstants.js";
import { isHealthFormComplete } from "./healthFormCatalog.js";

const TERMINAL = new Set([
  MEMBERSHIP_STATUSES.ACTIVE,
  MEMBERSHIP_STATUSES.EXPIRED,
  MEMBERSHIP_STATUSES.SUSPENDED,
  MEMBERSHIP_STATUSES.CANCELLED,
]);

export function emptyIdentity() {
  return {
    firstName: "",
    lastName: "",
    birthDate: "",
    address: "",
    city: "",
    province: "Québec",
    postalCode: "",
    phone: "",
    email: "",
    dojoId: "",
    instructorId: "",
  };
}

export function emptyEmergency() {
  return { name: "", relation: "", phone: "" };
}

export function emptyParent() {
  return {
    firstName: "",
    lastName: "",
    relation: "",
    phone: "",
    email: "",
    address: "",
    city: "",
    province: "Québec",
    postalCode: "",
  };
}

export function emptyConsents(kind = REGISTRATION_KIND.ADULT) {
  return {
    privacy: false,
    healthAccuracy: false,
    photoOptional: false,
    parentalAuthority: kind === REGISTRATION_KIND.MINOR ? false : true,
  };
}

/**
 * @param {object} record
 * @returns {{ complete: boolean, missing: string[] }}
 */
export function evaluateMembershipCompleteness(record) {
  const missing = [];
  const identity = record?.identity || {};
  const requiredIdentity = ["firstName", "lastName", "birthDate", "address", "city", "province", "postalCode", "phone", "dojoId"];
  if (record?.kind !== "minor") requiredIdentity.push("email");
  if (requiredIdentity.some((k) => !String(identity[k] ?? "").trim())) {
    missing.push("identity");
  }
  const emergency = record?.emergency || {};
  if (!String(emergency.name ?? "").trim() || !String(emergency.phone ?? "").trim() || !String(emergency.relation ?? "").trim()) {
    missing.push("emergency");
  }
  if (record?.kind === REGISTRATION_KIND.MINOR) {
    const parent = record?.parent || {};
    const requiredParent = ["firstName", "lastName", "relation", "phone", "email"];
    if (requiredParent.some((k) => !String(parent[k] ?? "").trim())) {
      missing.push("parent");
    }
  }
  if (!isHealthFormComplete(record?.health?.answers, record?.kind)) {
    missing.push("health");
  }
  const consents = record?.consents || {};
  if (!consents.privacy || !consents.healthAccuracy) missing.push("consents");
  if (record?.kind === REGISTRATION_KIND.MINOR && !consents.parentalAuthority) missing.push("consents");
  if (record?.payment?.status !== PAYMENT_STATUSES.CONFIRMED) missing.push("payment");
  return { complete: missing.length === 0, missing: [...new Set(missing)] };
}

export function deriveMembershipStatus(record, now = new Date()) {
  if (record?.status === MEMBERSHIP_STATUSES.CANCELLED) return MEMBERSHIP_STATUSES.CANCELLED;
  if (record?.status === MEMBERSHIP_STATUSES.SUSPENDED) return MEMBERSHIP_STATUSES.SUSPENDED;
  if (record?.status === MEMBERSHIP_STATUSES.ACTIVE) {
    if (record.periodEnd && record.periodEnd < now.toISOString().slice(0, 10)) {
      return MEMBERSHIP_STATUSES.EXPIRED;
    }
    return MEMBERSHIP_STATUSES.ACTIVE;
  }
  if (record?.status === MEMBERSHIP_STATUSES.EXPIRED) return MEMBERSHIP_STATUSES.EXPIRED;
  if (record?.status === MEMBERSHIP_STATUSES.PENDING_APPROVAL) return MEMBERSHIP_STATUSES.PENDING_APPROVAL;
  if (record?.status === MEMBERSHIP_STATUSES.DRAFT && !record?.identity?.firstName) {
    return MEMBERSHIP_STATUSES.DRAFT;
  }
  const { missing } = evaluateMembershipCompleteness(record);
  if (missing.includes("identity") || missing.includes("emergency") || missing.includes("parent")) {
    return MEMBERSHIP_STATUSES.FORM_INCOMPLETE;
  }
  if (missing.includes("health") || missing.includes("consents")) {
    return MEMBERSHIP_STATUSES.HEALTH_PENDING;
  }
  if (missing.includes("payment")) {
    return MEMBERSHIP_STATUSES.PAYMENT_PENDING;
  }
  if (record?.requiresApproval) return MEMBERSHIP_STATUSES.PENDING_APPROVAL;
  return MEMBERSHIP_STATUSES.PAYMENT_PENDING;
}

export function canActivateMembership(record) {
  return evaluateMembershipCompleteness(record).complete && record?.status !== MEMBERSHIP_STATUSES.CANCELLED;
}

export function isActiveMember(record, now = new Date()) {
  if (!record || record.status !== MEMBERSHIP_STATUSES.ACTIVE) return false;
  if (record.periodEnd && record.periodEnd < now.toISOString().slice(0, 10)) return false;
  return true;
}

/**
 * TEMP-POLICY-CALENDAR-YEAR : fin = 31 décembre de l'année de paiement.
 * @param {object} record
 * @param {Date} [now]
 */
export function activateMembership(record, now = new Date()) {
  if (!canActivateMembership(record)) {
    return {
      ok: false,
      record: { ...record, status: deriveMembershipStatus(record, now) },
    };
  }
  const year = now.getFullYear();
  const iso = now.toISOString();
  return {
    ok: true,
    record: {
      ...record,
      status: MEMBERSHIP_STATUSES.ACTIVE,
      activatedAt: iso,
      periodStart: iso.slice(0, 10),
      periodEnd: `${year}-12-31`,
      periodYear: year,
      periodPolicy: TEMP_PERIOD_POLICY,
      memberPermissionsGranted: true,
      adminPermissionsGranted: false,
      updatedAt: iso,
    },
  };
}

export function nextMissingStep(record) {
  const { missing } = evaluateMembershipCompleteness(record);
  if (missing.includes("identity") || missing.includes("emergency") || missing.includes("parent")) return "identity";
  if (missing.includes("health")) return "health";
  if (missing.includes("consents")) return "consents";
  if (missing.includes("payment")) return "payment";
  return "done";
}

export function membershipDisplayName(record) {
  const idn = record?.identity || {};
  return `${idn.firstName || ""} ${idn.lastName || ""}`.trim() || record?.id || "";
}

export { TERMINAL as TERMINAL_MEMBERSHIP_STATUSES };
